import React, { useState, useEffect, useRef } from 'react';
import { getLenderSettings, saveLenderSettings, deleteLenderSetting } from '../api/lenderApi';
import GmailConnection from '../components/GmailConnection';
import GeneralEmailSettings from '../components/GeneralEmailSettings';
import '../styles/LenderSettings.css';

const LenderSettings = () => {
  const emailSettingsRef = useRef();
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedLenders, setExpandedLenders] = useState(new Set());
  const [showAddLenderModal, setShowAddLenderModal] = useState(false);
  const [newLender, setNewLender] = useState({
    lenderName: '',
    financingType: 'working-capital',
    tier: 1,
    emailToAddress: '',
    emailCcAddress: '',
    emailSubject: 'New Submission For Review - {businessName}',
    emailBody: 'Hello {lenderName},\n\nPlease see the attached submission with supporting documentation for review. Thank you.\n\n{dealNotes}\n\nSubmission Notes:\n\nThank You,\nOperations Support',
    ccRecordOwner: false,
    crmAccountId: ''
  });

  useEffect(() => {
    loadLenderSettings();
  }, []);

  const loadLenderSettings = async () => {
    try {
      setLoading(true);
      const settings = await getLenderSettings();
      setLenders(settings);
      setError('');
    } catch (err) {
      console.error('Error loading lender settings:', err);
      setError('Failed to load lender settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLender = async (lenderId, lenderName) => {
    if (window.confirm(`Are you sure you want to delete ${lenderName}? This action cannot be undone.`)) {
      try {
        setSaving(true);
        console.log(`Attempting to delete lender: ${lenderId} (${lenderName})`);
        
        // Call the actual delete API endpoint
        await deleteLenderSetting(lenderId);
        
        // Remove from local state only after successful deletion
        const updatedLenders = lenders.filter(lender => lender.stringId !== lenderId);
        setLenders(updatedLenders);
        
        setSuccessMessage(`Lender '${lenderName}' deleted successfully!`);
        setError('');
        setTimeout(() => setSuccessMessage(''), 3000);
        
        console.log(`Successfully deleted lender: ${lenderId}`);
      } catch (err) {
        console.error('Error deleting lender:', err);
        const errorMessage = err.message || 'Failed to delete lender. Please try again.';
        setError(errorMessage);
        setSuccessMessage('');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveLenderSettings(lenders);
      setSuccessMessage('Lender settings saved successfully!');
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving lender settings:', err);
      setError('Failed to save lender settings. Please try again.');
      setSuccessMessage('');
    } finally {
      setSaving(false);
    }
  };

  const updateLenderSetting = (lenderId, field, value) => {
    setLenders(prevLenders =>
      prevLenders.map(lender =>
        lender.stringId === lenderId
          ? { ...lender, [field]: value }
          : lender
      )
    );
  };

  const toggleLenderExpansion = (lenderId) => {
    setExpandedLenders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lenderId)) {
        newSet.delete(lenderId);
      } else {
        newSet.add(lenderId);
      }
      return newSet;
    });
  };

  const filteredLenders = lenders.filter(lender => {
    if (filterType === 'all') return true;
    if (filterType === 'working-capital') return lender.financingType === 'working-capital';
    if (filterType === 'equipment-financing') return lender.financingType === 'equipment-financing';
    return true;
  });

  const getStatusBadge = (lender) => {
    if (lender.emailOnly) return <span className="status-badge email-only">Email Only</span>;
    return <span className="status-badge api">API</span>;
  };

  const getActiveStatusBadge = (lender) => {
    if (lender.active) return <span className="status-badge active">Active</span>;
    return <span className="status-badge inactive">Inactive</span>;
  };

  const getTierBadge = (tier) => {
    return <span className={`tier-badge tier-${tier}`}>Tier {tier}</span>;
  };

  const handleNewLenderChange = (field, value) => {
    setNewLender(prev => ({ ...prev, [field]: value }));
  };

  const handleAddLender = async () => {
    try {
      if (!newLender.lenderName.trim()) {
        setError('Lender name is required');
        return;
      }

      if (newLender.emailOnly && !newLender.emailToAddress.trim()) {
        setError('Email address is required for email lenders');
        return;
      }

      setSaving(true);
      setError('');

      // Generate stringId from lender name (lowercase, remove special chars)
      const stringId = newLender.lenderName.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 20);
      
      // Check if stringId already exists
      if (lenders.some(lender => lender.stringId === stringId)) {
        setError('A lender with this name already exists');
        setSaving(false);
        return;
      }

      // Create new lender with the correct settings
      const lenderToCreate = {
        ...newLender,
        stringId,
        active: true,
        watermarkEnabled: false,
        contactInfoScramble: false,
        ccRecordOwner: newLender.ccRecordOwner || false
      };

      // Add the new lender to the existing lenders array and save all
      const updatedLenders = [...lenders, lenderToCreate];
      await saveLenderSettings(updatedLenders);
      
      // Refresh the lenders list
      await loadLenderSettings();
      
      setSuccessMessage('New lender added successfully!');
      setShowAddLenderModal(false);
      resetNewLenderForm();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding new lender:', err);
      setError('Failed to add new lender. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetNewLenderForm = () => {
    setNewLender({
      lenderName: '',
      financingType: 'working-capital',
      tier: 1,
      emailToAddress: '',
      emailCcAddress: '',
      emailSubject: 'New Submission For Review - {businessName}',
      emailBody: 'Hello {lenderName},\n\nPlease see the attached submission with supporting documentation for review. Thank you.\n\n{dealNotes}\n\nSubmission Notes:\n\nThank You,\nOperations Support',
      ccRecordOwner: false,
      crmAccountId: ''
    });
  };

  const handleSaveEmailSettings = async () => {
    if (emailSettingsRef.current) {
      setSaving(true);
      const success = await emailSettingsRef.current.save();
      setSaving(false);
      
      if (success) {
        setSuccessMessage('Email settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    }
  };

  const closeModal = () => {
    setShowAddLenderModal(false);
    resetNewLenderForm();
    setError('');
  };

  if (loading) {
    return (
      <div className="field-mappings-container">
        <div className="loading-spinner">Loading lender settings...</div>
      </div>
    );
  }

  return (
    <div className="field-mappings-container lender-settings">
      <div className="header-section">
        <h2>Lender Settings</h2>
        <p className="subtitle">Configure lender availability, tiers, and email settings</p>
      </div>

      {/* Help Section */}
      <div className="help-section">
        <div className="help-item">
          <strong>Active:</strong> Controls whether the lender is available for submissions. Inactive lenders will not appear in the lender submission forms.
        </div>
        <div className="help-item">
          <strong>Email Only:</strong> Indicates whether this lender only accepts submissions via email rather than through API integration. Email-only lenders require manual processing.
        </div>
        <div className="help-item">
          <strong>Tier:</strong> Priority level for lender display (1 = highest priority, 3 = lowest priority). Lower tier numbers appear first in the submission interface.
        </div>
        <div className="help-item">
          <strong>Watermark:</strong> Adds your company logo diagonally across each page of bank statements that are submitted to lenders. This helps with branding and document tracking.
        </div>
        <div className="help-item">
          <strong>Contact Info Scramble:</strong> Protects sensitive information by modifying contact details before submitting to lenders. Enable or disable these features for each lender individually using the controls below.
        </div>
        <div className="help-item">
          <strong>CC Record Owner:</strong> For email-only lenders, this option will automatically CC the record owner (Salesforce contact associated with the deal) on submission emails when enabled.
        </div>
        <div className="help-item">
          <strong>Email Templates:</strong> You can use placeholders in email subjects and bodies: <code>{`{lenderName}`}</code> (name of the lender), <code>{`{businessName}`}</code> (company name), <code>{`{requestedAmount}`}</code> (funding amount), <code>{`{dealNotes}`}</code> (deal notes), and <code>{`{clientName}`}</code> (your company name).
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <label>Filter Lenders:</label>
        <div className="filter-options">
          <label>
            <input
              type="radio"
              value="all"
              checked={filterType === 'all'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            All
          </label>
          <label>
            <input
              type="radio"
              value="working-capital"
              checked={filterType === 'working-capital'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            Working Capital
          </label>
          <label>
            <input
              type="radio"
              value="equipment-financing"
              checked={filterType === 'equipment-financing'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            Equipment Financing
          </label>
        </div>
      </div>

            {/* Error/Success Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* General Email Settings Section */}
      <GeneralEmailSettings ref={emailSettingsRef} />

      {/* Add Lender Buttons */}
      <div className="add-lender-section">
        <button 
          className="add-lender-btn" 
          onClick={() => {
            setNewLender(prev => ({ ...prev, emailOnly: true }));
            setShowAddLenderModal(true);
          }}
        >
          + Add Email Lender
        </button>
        <button 
          className="add-lender-btn" 
          onClick={() => {
            setNewLender(prev => ({ ...prev, emailOnly: false }));
            setShowAddLenderModal(true);
          }}
        >
          + Add API Lender
        </button>
      </div>

      {/* Lenders List */}
      <div className="lenders-list">
        {filteredLenders.map((lender) => (
          <div key={lender.stringId} className={`lender-row ${expandedLenders.has(lender.stringId) ? 'expanded' : ''}`}>
            <div 
              className={`lender-header ${expandedLenders.has(lender.stringId) ? 'expanded' : ''}`}
              onClick={() => toggleLenderExpansion(lender.stringId)}
            >
              <div className="lender-info">
                <div className="lender-name-section">
                  <h3>{lender.lenderName}</h3>
                </div>
                <div className="lender-badges">
                  <span className="lender-id-badge">ID: {lender.uniqueLenderId}</span>
                  {lender.watermarkEnabled && <span className="feature-badge">Watermark ✓</span>}
                  {lender.contactInfoScramble && <span className="feature-badge">Scramble ✓</span>}
                  {lender.emailOnly && lender.ccRecordOwner && <span className="feature-badge">CC Owner ✓</span>}
                  {getActiveStatusBadge(lender)}
                  {getStatusBadge(lender)}
                  {getTierBadge(lender.tier)}
                  <span className="financing-type">{lender.financingType === 'working-capital' ? 'Working Capital' : 'Equipment Financing'}</span>
                </div>
              </div>
              <div className="expand-icon">
                {expandedLenders.has(lender.stringId) ? '▼' : '▶'}
              </div>
            </div>

            {expandedLenders.has(lender.stringId) && (
              <div className="lender-details">
                <div className="settings-grid">
                  {/* Basic Settings */}
                  <div className="settings-section">
                    <h4>Basic Settings</h4>
                    
                    <div className="setting-item">
                      <label>ACTIVE</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.active}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'active', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>EMAIL ONLY</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.emailOnly}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'emailOnly', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>TIER</label>
                      <select
                        value={lender.tier}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'tier', parseInt(e.target.value))}
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                      </select>
                    </div>
                  </div>

                  {/* Security Settings */}
                  <div className="settings-section">
                    <h4>Security Settings</h4>
                    
                    <div className="setting-item">
                      <label>WATERMARK</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.watermarkEnabled}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'watermarkEnabled', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>CONTACT SCRAMBLE</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.contactInfoScramble}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'contactInfoScramble', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    {(lender.emailOnly || (lender.emailToAddress && lender.emailToAddress.trim() !== '')) && (
                      <div className="setting-item">
                        <label>CC RECORD OWNER</label>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={lender.ccRecordOwner}
                            onChange={(e) => updateLenderSetting(lender.stringId, 'ccRecordOwner', e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Salesforce Integration */}
                  <div className="settings-section">
                    <h4>Salesforce Integration</h4>
                    
                    <div className="setting-item">
                      <label>SALESFORCE ACCOUNT ID</label>
                      <input
                        type="text"
                        placeholder="Salesforce Account ID (e.g., 001f200001WZcNv)"
                        value={lender.crmAccountId || ''}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'crmAccountId', e.target.value)}
                      />
                      <small className="help-text">
                        The Salesforce Account ID for this lender. Used for posting submissions to Salesforce.
                      </small>
                    </div>
                  </div>

                  {/* Email Settings */}
                  <div className="settings-section email-settings">
                    <h4>Email Settings</h4>
                    
                    <div className="setting-item">
                      <label>EMAIL TO ADDRESS</label>
                      <input
                        type="email"
                        placeholder="Optional"
                        value={lender.emailToAddress || ''}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'emailToAddress', e.target.value)}
                      />
                    </div>

                    <div className="setting-item">
                      <label>EMAIL CC ADDRESS</label>
                      <input
                        type="email"
                        placeholder="Optional"
                        value={lender.emailCcAddress || ''}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'emailCcAddress', e.target.value)}
                      />
                    </div>

                                         <div className="setting-item">
                                             <label>EMAIL SUBJECT</label>
                      <input
                        type="text"
                        placeholder="Default: New Submission For Review - {businessName}"
                        value={lender.emailSubject || ''}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'emailSubject', e.target.value)}
                      />
                     </div>

                     <div className="setting-item">
                       <label>EMAIL BODY</label>
                       <textarea
                         placeholder="Default: Hello {lenderName},&#10;&#10;Please see the attached submission with supporting documentation for review. Thank you.&#10;&#10;Submission Notes:&#10;&#10;Thank You,&#10;Operations Support"
                         value={lender.emailBody || ''}
                         onChange={(e) => updateLenderSetting(lender.stringId, 'emailBody', e.target.value)}
                         rows={6}
                       />
                     </div>
                  </div>
                </div>

                <div className="lender-actions">
                  <button
                    className="save-btn"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteLender(lender.stringId, lender.lenderName)}
                    disabled={saving}
                  >
                    Delete Lender
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredLenders.length === 0 && (
        <div className="no-lenders">
          <p>No lenders found for the selected filter.</p>
        </div>
      )}

      {/* Save Email Settings Button */}
      <div className="save-email-settings-section">
        <button
          className="save-email-settings-btn"
          onClick={handleSaveEmailSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Email Settings'}
        </button>
      </div>

      {/* Add Lender Modal */}
      {showAddLenderModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* <div className="modal-header">
              <h3>Add New Email Lender</h3>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div> */}
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-section">
                  <h4>Basic Information</h4>
                  
                  <div className="form-item">
                    <label>LENDER NAME *</label>
                    <input
                      type="text"
                      value={newLender.lenderName}
                      onChange={(e) => handleNewLenderChange('lenderName', e.target.value)}
                      placeholder="Enter lender name"
                      required
                    />
                  </div>

                  <div className="form-item">
                    <label>FINANCING TYPE</label>
                    <select
                      value={newLender.financingType}
                      onChange={(e) => handleNewLenderChange('financingType', e.target.value)}
                    >
                      <option value="working-capital">Working Capital</option>
                      <option value="equipment-financing">Equipment Financing</option>
                    </select>
                  </div>

                  <div className="form-item">
                    <label>TIER</label>
                    <select
                      value={newLender.tier}
                      onChange={(e) => handleNewLenderChange('tier', parseInt(e.target.value))}
                    >
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                    </select>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Salesforce Integration</h4>
                  
                  <div className="form-item">
                    <label>SALESFORCE ACCOUNT ID</label>
                    <input
                      type="text"
                      value={newLender.crmAccountId || ''}
                      onChange={(e) => handleNewLenderChange('crmAccountId', e.target.value)}
                      placeholder="Salesforce Account ID (optional)"
                    />
                    <small className="help-text">
                      The Salesforce Account ID for this lender. Used for posting submissions to Salesforce.
                    </small>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Email Configuration</h4>
                  
                  <div className="form-item">
                    <label>EMAIL TO ADDRESS {newLender.emailOnly ? '*' : ''}</label>
                    <input
                      type="email"
                      value={newLender.emailToAddress}
                      onChange={(e) => handleNewLenderChange('emailToAddress', e.target.value)}
                      placeholder="lender@example.com"
                      required={newLender.emailOnly}
                    />
                  </div>

                  <div className="form-item">
                    <label>EMAIL CC ADDRESS</label>
                    <input
                      type="email"
                      value={newLender.emailCcAddress}
                      onChange={(e) => handleNewLenderChange('emailCcAddress', e.target.value)}
                      placeholder="cc@example.com (optional)"
                    />
                  </div>

                                     <div className="form-item">
                     <label>EMAIL SUBJECT</label>
                     <input
                       type="text"
                       value={newLender.emailSubject}
                       onChange={(e) => handleNewLenderChange('emailSubject', e.target.value)}
                       placeholder="New Submission For Review - {businessName}"
                     />
                   </div>

                  <div className="form-item">
                    <label>EMAIL BODY</label>
                    <textarea
                      value={newLender.emailBody}
                      onChange={(e) => handleNewLenderChange('emailBody', e.target.value)}
                      placeholder="Hello {lenderName},&#10;&#10;Please see the attached submission with supporting documentation for review. Thank you.&#10;&#10;Submission Notes:&#10;&#10;Thank You,&#10;Operations Support"
                      rows={6}
                    />
                  </div>

                  {(newLender.emailOnly || (newLender.emailToAddress && newLender.emailToAddress.trim() !== '')) && (
                    <div className="form-item">
                      <label>CC RECORD OWNER</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={newLender.ccRecordOwner}
                          onChange={(e) => handleNewLenderChange('ccRecordOwner', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="save-btn" 
                onClick={handleAddLender}
                disabled={saving || !newLender.lenderName.trim() || (newLender.emailOnly && !newLender.emailToAddress.trim())}
              >
                {saving ? 'Adding...' : 'Add Lender'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Gmail Connection Section */}
      <GmailConnection />
    </div>
  );
};

export default LenderSettings; 